import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HiOutlineDocumentText, 
  HiOutlineShieldCheck, 
  HiOutlineReceiptRefund, 
  HiOutlineXCircle, 
  HiOutlinePhone,
  HiOutlineChevronRight,
  HiOutlineEnvelope
} from 'react-icons/hi2';

const navItems = [
  {
    path: '/terms-conditions',
    label: 'Terms & Conditions',
    shortLabel: 'Terms',
    icon: HiOutlineDocumentText,
    desc: 'Usage terms & binding agreement'
  },
  {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    shortLabel: 'Privacy',
    icon: HiOutlineShieldCheck,
    desc: 'How we collect & protect data'
  },
  {
    path: '/refund-policy',
    label: 'Refund Policy',
    shortLabel: 'Refunds',
    icon: HiOutlineReceiptRefund,
    desc: 'Returns, refunds & eligibility'
  },
  {
    path: '/cancellation-policy',
    label: 'Cancellation Policy',
    shortLabel: 'Cancellation',
    icon: HiOutlineXCircle,
    desc: 'Order & subscription cancellation'
  },
  {
    path: '/contactus',
    label: 'Contact Us',
    shortLabel: 'Contact',
    icon: HiOutlinePhone,
    desc: 'Addresses, phone & support email'
  }
];

const LegalLayout = ({ title, subtitle, lastUpdated, sections = [], children }) => {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-slate-50/60 pt-16 pb-16">
      {/* Subtle, Professional Top Header (No top chip, subtle grey last updated) */}
      <div className="bg-white border-b border-gray-200/80 py-8 sm:py-10 px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm sm:text-base text-gray-600 font-normal mt-2 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p className="text-xs text-gray-400 font-normal mt-2">
              Last Updated: {lastUpdated}
            </p>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Mobile Horizontal Navigation Bar (Without sidebar on mobile) */}
        <div className="lg:hidden mb-6 -mx-4 px-4 overflow-x-auto no-scrollbar py-1">
          <div className="flex space-x-2 min-w-max pb-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-[#FF410B] text-white rounded-full'
                      : 'bg-white text-gray-700 hover:text-[#FF410B] border border-gray-200 rounded-full'
                  }`}
                >
                  <Icon className="text-sm" />
                  <span>{item.shortLabel}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* Desktop Left Sidebar (Only visible on lg: and up) */}
          <aside className="hidden lg:block w-72 shrink-0 sticky top-24 space-y-6">
            
            {/* Policy Navigation Panel */}
            <div className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-xs space-y-1">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-3 py-2">
                Policy
              </h2>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`group flex items-center justify-between p-2.5 rounded-lg text-xs font-semibold transition-all ${
                      isActive
                        ? 'bg-[#FF410B] text-white'
                        : 'text-gray-700 hover:bg-orange-50/70 hover:text-[#FF410B]'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className={`text-base shrink-0 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-[#FF410B]'}`} />
                      <span className="truncate">{item.label}</span>
                    </div>
                    <HiOutlineChevronRight className={`text-xs shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  </Link>
                );
              })}
            </div>

            {/* Quick Table of Contents (if sections are provided) */}
            {sections.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-gray-200/80 shadow-xs">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                  On this page
                </h3>
                <nav className="space-y-1.5">
                  {sections.map((sec) => (
                    <a
                      key={sec.id}
                      href={`#${sec.id}`}
                      className="block text-xs text-gray-600 hover:text-[#FF410B] transition-colors py-0.5 pl-2 border-l-2 border-transparent hover:border-[#FF410B]"
                    >
                      {sec.title}
                    </a>
                  ))}
                </nav>
              </div>
            )}

            {/* Support Help Box */}
            <div className="bg-white rounded-xl p-4 border border-gray-200/80 shadow-xs space-y-3">
              <div>
                <h4 className="text-xs font-bold text-gray-900">Need Support?</h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Have questions regarding your order or policies?
                </p>
              </div>
              <a
                href="mailto:hello@falhari.com"
                className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold border-2 border-black bg-black text-white hover:bg-gray-800 transition py-2.5 px-4 rounded-none text-center"
              >
                <HiOutlineEnvelope className="text-sm" />
                <span>Email Support</span>
              </a>
            </div>

          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0 bg-white rounded-2xl border border-gray-200/80 shadow-xs p-6 sm:p-8">
            {children}
          </main>

        </div>
      </div>
    </div>
  );
};

export default LegalLayout;
